import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { VabsArRateChartPeriod, requestsClient } from '@/shared/api';
import { ICurrencyAccount, useVabsAccountsStore } from '@/entities/currency-account';
import { useExchangeRates } from '@/entities/rates/model';
import { useCompanyIdStore } from '@/entities/user';

import { ExchangeRates } from '@/features/exchange';
import { debounce } from '@/shared/lib';

import { ExchangeFormValues, exchangeFormSchema } from './schema';

export type RecalculateSide = 'from' | 'to';

const isNumber = (value: unknown): value is number => {
  return typeof value === 'number' && Number.isFinite(value);
};

const toNumber = (value: unknown): number | null => {
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
};

const roundAmount = (value: number) => parseFloat(value.toFixed(2));

interface FxQuotePayload {
  sourceAmount: string;
  sourceCurrencyTicker: string;
  targetCurrencyTicker: string;
  virtualAccountId: string;
}

interface ExchangeFeePayload {
  senderAccountId: string;
  recipientAccountId: string;
  recipientClientId: string;
  sourceCurrencyId: string;
  targetCurrencyId: string;
  quoteId: string;
  sourceAmount?: string;
  targetAmount?: string;
}

interface CreateExchangePaymentPayload {
  senderAccountId: string;
  recipientAccountId: string;
  sourceCurrencyId: string;
  targetCurrencyId: string;
  recipientClientId: string;
  quoteId: string;
  sourceAmount?: string;
  targetAmount?: string;
}

interface CalculateExchangeInput {
  side: RecalculateSide;
  source: ICurrencyAccount;
  target: ICurrencyAccount;
  exchangeFrom: ICurrencyAccount;
  exchangeTo: ICurrencyAccount;
  inputAmount: number;
}

const fetchFxQuote = async (input: FxQuotePayload) => {
  const { vabsMyFxQuote } = await requestsClient.vabsMyFxQuote({ input });
  return vabsMyFxQuote;
};

const fetchExchangeFee = async (input: ExchangeFeePayload) => {
  const { vabsCalculateMyExchangePaymentFee } =
    await requestsClient.vabsCalculateMyExchangePaymentFee({ input });
  return vabsCalculateMyExchangePaymentFee;
};

const createExchangePaymentRequest = async (input: CreateExchangePaymentPayload) => {
  return requestsClient.vabsCreateMyExchangePayment({ input });
};

export const useExchangeFxQuoteMutation = () => {
  return useMutation({
    mutationKey: ['exchange', 'fx-quote'],
    mutationFn: fetchFxQuote,
  });
};

export const useExchangeFeeMutation = () => {
  return useMutation({
    mutationKey: ['exchange', 'fee'],
    mutationFn: fetchExchangeFee,
  });
};

export const useCreateExchangePaymentMutation = () => {
  return useMutation({
    mutationKey: ['exchange', 'create'],
    mutationFn: createExchangePaymentRequest,
  });
};

export const useExchangeCalculator = () => {
  const accounts = useVabsAccountsStore((state) => state.accounts);
  const companyId = useCompanyIdStore((state) => state.companyId);
  const isPrefilled = useRef(false);
  const isManualRecalculate = useRef(false);
  const recalculateRequestId = useRef(0);
  const [recalculateSide, setRecalculateSide] = useState<RecalculateSide>('from');
  const [recalculationError, setRecalculationError] = useState('');

  const { mutateAsync: fetchFxQuoteAsync, isPending: isQuotePending } = useExchangeFxQuoteMutation();
  const { mutateAsync: fetchExchangeFeeAsync, isPending: isFeePending } =
    useExchangeFeeMutation();

  const form = useForm<ExchangeFormValues>({
    resolver: zodResolver(exchangeFormSchema),
    defaultValues: {
      fromCurrency: {},
      toCurrency: {},
      fromAmount: 0,
      toAmount: 0,
      rate: 1,
      fee: 0,
      lastEditedSide: 'from',
    },
  });

  const { control, setValue } = form;
  const fromCurrencyCode = 'EUR';
  const toCurrencyCode = 'USD';

  const fromCurrency = useWatch({ control, name: 'fromCurrency' });
  const toCurrency = useWatch({ control, name: 'toCurrency' });
  const fromAmount = useWatch({ control, name: 'fromAmount' });
  const toAmount = useWatch({ control, name: 'toAmount' });
  const fee = useWatch({ control, name: 'fee' });

  const { data: liveRates = [] } = useExchangeRates({
    fromCurrency,
    toCurrency,
    chartPeriod: VabsArRateChartPeriod.All,
  });

  const exchangeRates: ExchangeRates = {
    from: fromCurrency?.code || fromCurrencyCode,
    to: toCurrency?.code || toCurrencyCode,
    rates: liveRates,
  };

  const lastRate = exchangeRates.rates[exchangeRates.rates.length - 1];
  const rateLabel =
    Number.isFinite(lastRate?.rate) && lastRate?.rate ? lastRate.rate.toFixed(2) : '--';

  const markRecalculate = useCallback((side: RecalculateSide) => {
    isManualRecalculate.current = true;
    recalculateRequestId.current += 1;
    setRecalculateSide(side);
    setValue('lastEditedSide', side);
  }, [setValue]);

  const calculateExchange = useCallback(
    async ({ side, source, target, exchangeFrom, exchangeTo, inputAmount }: CalculateExchangeInput) => {
      if (!Number.isFinite(inputAmount) || inputAmount <= 0) {
        if (side === 'from') {
          setValue('toAmount', 0);
        } else {
          setValue('fromAmount', 0);
        }
        setValue('fee', 0);
        setRecalculateSide(side);
        return;
      }

      const requestId = ++recalculateRequestId.current;
      setRecalculationError('');

      try {
        const vabsMyFxQuote = await fetchFxQuoteAsync({
          sourceAmount: inputAmount.toString(),
          sourceCurrencyTicker: source.code,
          targetCurrencyTicker: target.code,
          virtualAccountId: source.virtualId,
        });
        if (requestId !== recalculateRequestId.current) return;

        const quoteRate = toNumber(vabsMyFxQuote.exchangeRate);
        const quoteSourceAmount = toNumber(vabsMyFxQuote.sourceAmount);
        const quoteTargetAmount = toNumber(vabsMyFxQuote.targetAmount);
        if (quoteRate === null || quoteSourceAmount === null || quoteTargetAmount === null) {
          throw new Error('Invalid quote data');
        }

        const convertedRate = side === 'from' ? quoteRate : quoteRate ? 1 / quoteRate : 0;
        const nextFromAmount = side === 'from'
          ? roundAmount(quoteSourceAmount)
          : roundAmount(quoteTargetAmount);
        const nextToAmount = side === 'from'
          ? roundAmount(quoteTargetAmount)
          : roundAmount(inputAmount);
        const feeSourceAmount = side === 'from'
          ? quoteSourceAmount
          : quoteTargetAmount;

        setValue('rate', roundAmount(convertedRate));
        setValue('fromAmount', nextFromAmount);
        setValue('toAmount', nextToAmount);

        const vabsCalculateMyExchangePaymentFee = await fetchExchangeFeeAsync({
          senderAccountId: exchangeFrom.virtualId || '',
          recipientAccountId: exchangeTo.virtualId || '',
          recipientClientId: companyId || '',
          sourceCurrencyId: exchangeFrom.currencyId || '',
          targetCurrencyId: exchangeTo.currencyId || '',
          quoteId: vabsMyFxQuote.id,
          sourceAmount: feeSourceAmount.toString(),
        });
        if (requestId !== recalculateRequestId.current) return;

        const nextFee = toNumber(vabsCalculateMyExchangePaymentFee.originalTotalFee);
        setValue('fee', isNumber(nextFee) ? nextFee : 0);
      } catch (error) {
        if (requestId !== recalculateRequestId.current) return;
        console.log('exchange calculator recalculate error', error);
        setRecalculationError('Failed to recalculate amount and fees');
        setValue('fee', 0);
      }
    },
    [companyId, setValue, fetchExchangeFeeAsync, fetchFxQuoteAsync],
  );

  const debouncedRecalculate = useMemo(
    () =>
      debounce((payload: Parameters<typeof calculateExchange>[0]) => {
        void calculateExchange(payload);
      }, 650),
    [calculateExchange],
  );

  const requestRecalculate = useCallback(() => {
    if (!isManualRecalculate.current) {
      return;
    }

    isManualRecalculate.current = false;

    if (!fromCurrency?.code || !toCurrency?.code || !fromCurrency?.virtualId || !toCurrency?.virtualId) {
      return;
    }

    if (recalculateSide === 'from') {
      debouncedRecalculate({
        side: 'from',
        source: fromCurrency,
        target: toCurrency,
        exchangeFrom: fromCurrency,
        exchangeTo: toCurrency,
        inputAmount: fromAmount,
      });
      return;
    }

    debouncedRecalculate({
      side: 'to',
      source: toCurrency,
      target: fromCurrency,
      exchangeFrom: fromCurrency,
      exchangeTo: toCurrency,
      inputAmount: toAmount,
    });
  }, [debouncedRecalculate, fromAmount, fromCurrency, recalculateSide, toAmount, toCurrency]);

  useEffect(() => {
    if (isPrefilled.current || !accounts.length) return;

    const activeAccounts = accounts.filter((item) => item.status === 'ACTIVE');
    if (!activeAccounts.length) return;

    const fromDefault =
      activeAccounts.find((item) => item.code === fromCurrencyCode) || activeAccounts[0];
    const toDefault =
      activeAccounts.find((item) => item.code === toCurrencyCode) ||
      activeAccounts.find((item) => item.id !== fromDefault.id) ||
      activeAccounts[0];

    setValue('fromCurrency', fromDefault);
    setValue('toCurrency', toDefault.code === fromDefault.code ? fromDefault : toDefault);
    isPrefilled.current = true;
  }, [accounts, setValue, fromCurrencyCode, toCurrencyCode]);

  useEffect(() => {
    if (recalculateSide !== 'from') return;
    requestRecalculate();
  }, [fromAmount, fromCurrency?.code, toCurrency?.code, recalculateSide, requestRecalculate]);

  useEffect(() => {
    if (recalculateSide !== 'to') return;
    requestRecalculate();
  }, [toAmount, fromCurrency?.code, toCurrency?.code, recalculateSide, requestRecalculate]);

  const handleSwap = useCallback(() => {
    markRecalculate('from');
    setValue('fromAmount', toAmount);
    setValue('toAmount', fromAmount);
    setValue('fromCurrency', toCurrency);
    setValue('toCurrency', fromCurrency);
  }, [markRecalculate, toAmount, fromAmount, toCurrency, fromCurrency, setValue]);

  return {
    form,
    exchangeRates,
    rateLabel,
    fromCurrency,
    toCurrency,
    fromAmount,
    toAmount,
    fee,
    recalculateSide,
    isRecalculating: isQuotePending || isFeePending,
    recalculationError,
    fromCurrencyCode,
    toCurrencyCode,
    markRecalculate,
    handleSwap,
  };
};
